import { useState, useEffect } from 'react';
import { Container, Header, Form, Dropdown, Input, TextArea, Button, Message, Divider, Loader, Segment } from 'semantic-ui-react';
import { useQuery, useMutation, gql } from '@apollo/client';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';
import { useIsAdmin } from '../../../hooks/isAdmin';
import { useHasMultipleOrganisations } from '../../../hooks/organisations';
import { useBlocks } from '../../../hooks/useBlocks';
import { GET_ME } from '../../../queries/getMe';

const GET_BLOCK_TAGS = gql`
  query BlockTags {
    blockTags {
      id
      name
      code
    }
  }
`;

const SET_ORGANISATION_BLOCKS = gql`
  mutation SetOrganisationBlocks($currentBlockTagId: Int, $nextBlockTagId: Int) {
    setOrganisationBlocks(currentBlockTagId: $currentBlockTagId, nextBlockTagId: $nextBlockTagId) {
      currentBlockTagId
      nextBlockTagId
    }
  }
`;

const SEND_ANNOUNCEMENT = gql`
  mutation SendAnnouncement($kind: String!, $title: String!, $body: String!) {
    sendAnnouncement(kind: $kind, title: $title, body: $body) {
      id
    }
  }
`;

const ANNOUNCEMENT_RECIPIENT_COUNT = gql`
  query AnnouncementRecipientCount($kind: String!) {
    announcementRecipientCount(kind: $kind) {
      subscribers
      recipients
    }
  }
`;

const KIND_OPTIONS = [
  { key: 'APPLICATION', value: 'APPLICATION', text: 'Application Announcement' },
  { key: 'PRODUCT',     value: 'PRODUCT',     text: 'Product Announcement' },
];

const NONE = '__none__';

const STREAMS_GATE = gql`
  query StreamsGate {
    streams { id }
  }
`;

const CAN_ADMINISTER_ROLES = gql`
  query CanAdministerRoles {
    canAdministerRoles
    canCreateOrganisation
  }
`;

export const AdminSettings = () => {
  const isAdmin = useIsAdmin();
  const hasMultipleOrganisations = useHasMultipleOrganisations();
  const { currentBlockTagId, nextBlockTagId, loading: blocksLoading } = useBlocks();

  // Skipped for non-admins. The page renders for everybody now, but these two queries back the
  // admin-only sections below — firing them for a teacher or a membership officer would mean a
  // request that can only fail, on every visit.
  const { data: blockTagsData, loading: tagsLoading } = useQuery(GET_BLOCK_TAGS, { skip: !isAdmin });
  const { data: rolesGate } = useQuery(CAN_ADMINISTER_ROLES);
  const canAdministerRoles = rolesGate?.canAdministerRoles === true;
  const canCreateOrganisation = rolesGate?.canCreateOrganisation === true;
  const { data: streamsGate } = useQuery(STREAMS_GATE);
  const hasStreams = (streamsGate?.streams?.length ?? 0) > 0;
  const [setOrganisationBlocks, { loading: saving, error }] = useMutation(SET_ORGANISATION_BLOCKS);

  const [currentId, setCurrentId] = useState<number | null>(null);
  const [nextId, setNextId] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  // Push notification announcement
  const [kind, setKind] = useState<'APPLICATION' | 'PRODUCT'>('APPLICATION');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const [sendAnnouncement, { loading: sending, error: sendError }] = useMutation(SEND_ANNOUNCEMENT);

  const { data: countData, loading: countLoading } = useQuery(ANNOUNCEMENT_RECIPIENT_COUNT, {
    variables: { kind },
    skip: !isAdmin,
  });

  useEffect(() => {
    if (!blocksLoading) {
      setCurrentId(currentBlockTagId);
      setNextId(nextBlockTagId);
    }
  }, [blocksLoading, currentBlockTagId, nextBlockTagId]);

  // Deliberately **not** `if (!isAdmin) return null`, which this page used to open with.
  // A supporter organisation's leadership and membership officer are not admins, and they are
  // exactly the people most likely to belong to two organisations and need the switcher below.
  // Every section carries its own gate, so a non-admin simply sees fewer of them.

  const tags = [...((blockTagsData?.blockTags ?? []) as { id: number; name: string; code: string }[])]
    .sort((a, b) => a.id - b.id);

  const tagOptions = tags.map((t) => ({
    key: t.id,
    value: t.id,
    text: `${t.name} (${t.code})`,
  }));

  const nextTagOptions = [
    { key: 'none', value: NONE, text: '— none —' },
    ...tagOptions,
  ];

  const handleSave = async () => {
    setSaved(false);
    await setOrganisationBlocks({
      variables: { currentBlockTagId: currentId, nextBlockTagId: nextId },
      refetchQueries: [{ query: GET_ME }],
    });
    setSaved(true);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    await sendAnnouncement({ variables: { kind, title, body } });
    setSent(true);
    setTitle('');
    setBody('');
  };

  const count = countData?.announcementRecipientCount as { subscribers: number; recipients: number } | undefined;

  const recipientSummary = () => {
    if (countLoading) {
      return <span><Loader active inline size="tiny" /> Counting recipients…</span>;
    }
    if (!count || count.subscribers === 0) {
      return <span>No subscribed app users yet for this type.</span>;
    }
    return (
      <span>
        ≈ <strong>{count.recipients}</strong> app user{count.recipients === 1 ? '' : 's'} will receive this
        {' '}— {count.subscribers} subscribed, {count.recipients} with the Joyya app installed.
      </span>
    );
  };

  return (
    <ResponsiveContainer>
      <Container style={{ paddingTop: '2em', maxWidth: '600px' }}>
        <Header as="h1">Settings</Header>

        {/* Shown only to people who belong to more than one organisation. The same entry is in
            the user menu, which is where people look first; this one is here because the
            "you've been added to an organisation" email points at Settings. */}
        {hasMultipleOrganisations && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Your organisations</Header>
              <p style={{ color: '#767676' }}>
                You belong to more than one. Switch between them here.
              </p>
              <Button as="a" href={PATHS.ORGANISATIONS}>Switch organisation</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* One person holds `CREATE_ORGANISATION` — deliberately not `canAdministerRoles`,
            which every organisation's administrator holds for their own organisation. */}
        {canCreateOrganisation && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Supporting organisations</Header>
              <p style={{ color: '#767676' }}>
                Bring a church or partner organisation into Joyya and invite their people.
              </p>
              <Button as="a" href={PATHS.ADMIN_SUPPORTER_INVITE}>
                Invite a supporting organisation
              </Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* Only shown to whoever administers the organisation — after Phase 9 that is the
            authority over *who can do what*, separate from managing the data itself. The server
            decides via `canAdministerRoles`; this just avoids a link that leads to a redirect. */}
        {canAdministerRoles && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Roles and permissions</Header>
              <p style={{ color: '#767676' }}>
                What each role grants, and who holds it.
              </p>
              <Button as="a" href={PATHS.ADMIN_ROLES}>Manage roles</Button>
            </Segment>
            <Divider />

            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Class code rules</Header>
              <p style={{ color: '#767676' }}>
                Which stream, program and class label combinations a class code may use.
              </p>
              <Button as="a" href={PATHS.ADMIN_SKU_RULES}>Manage class codes</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* Shown to anyone who can reach a stream, not only administrators — content reviewers
            manage streams, and the page gates itself on the same answer. */}
        {hasStreams && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Streams</Header>
              <p style={{ color: '#767676' }}>
                Where posts are published, and who can read them.
              </p>
              <Button as="a" href={PATHS.ADMIN_STREAMS}>Manage streams</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* These two were gated by the page-level admin check that used to sit at the top.
            Now that the page renders for everybody, they carry it themselves. */}
        {isAdmin && (
          <>
        <Header as="h2">Block Settings</Header>
        <Form success={saved} error={!!error}>
          <Form.Field>
            <label>Current block</label>
            <Dropdown
              selection
              loading={tagsLoading || blocksLoading}
              options={tagOptions}
              value={currentId ?? undefined}
              placeholder="Select current block..."
              onChange={(_, { value }) => {
                const v = value as number;
                setCurrentId(v);
                if (v === nextId) setNextId(null);
                setSaved(false);
              }}
            />
          </Form.Field>
          <Form.Field>
            <label>Next block</label>
            <Dropdown
              selection
              loading={tagsLoading || blocksLoading}
              options={nextTagOptions}
              value={nextId != null ? nextId : NONE}
              onChange={(_, { value }) => {
                setNextId(value === NONE ? null : (value as number));
                setSaved(false);
              }}
            />
          </Form.Field>
          {saved && <Message success content="Settings saved." />}
          {error && <Message error content={error.message} />}
          <Button primary loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Form>

        <Divider section />

        <Header as="h2">
          Push Notifications
          <Header.Subheader>
            Send a push notification to app users in supporting organisations who are
            subscribed to this topic, on both iOS and Android. Only users with the app
            installed and notifications enabled will receive it.
          </Header.Subheader>
        </Header>
        <Form success={sent} error={!!sendError}>
          <Form.Field>
            <label>Type</label>
            <Dropdown
              selection
              options={KIND_OPTIONS}
              value={kind}
              onChange={(_, { value }) => { setKind(value as 'APPLICATION' | 'PRODUCT'); setSent(false); }}
            />
            <div style={{ marginTop: '0.5em', color: 'rgba(0,0,0,0.6)' }}>
              {recipientSummary()}
            </div>
          </Form.Field>
          <Form.Field>
            <label>Title</label>
            <Input
              value={title}
              placeholder="Announcement title"
              onChange={(e) => { setTitle(e.target.value); setSent(false); }}
            />
          </Form.Field>
          <Form.Field>
            <label>Body</label>
            <TextArea
              value={body}
              placeholder="Announcement body"
              rows={4}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setBody(e.target.value); setSent(false); }}
            />
          </Form.Field>
          {sent && <Message success content="Announcement sent!" />}
          {sendError && <Message error content={sendError.message} />}
          <Button
            primary
            loading={sending}
            disabled={!title.trim() || !body.trim()}
            onClick={handleSend}
          >
            Send
          </Button>
        </Form>
          </>
        )}
      </Container>
    </ResponsiveContainer>
  );
};
